import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteTreatmentPlan } from 'api/TreatmentPlan';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { useTableContext } from 'lib/context/TableContext/TableContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { io } from 'lib/helpers/utility';
import { useTreatmentPlan } from 'lib/hooks/queries/treatmentPlan/useTreatmentPlan';

import TPModal from 'components/practice/charts/ClinicalNote/TreatmentPlan/TPModal';
import SignModal from 'components/practice/charts/ClinicalNote/shared/Sign/SignModal';
import { CustomStatusBarCount } from 'components/shared/AGTable/CustomStatusBarCount';
import TableFilters from 'components/shared/AgTablePage/components/TableFilters';
import ViewTabs from 'components/shared/AgTablePage/components/ViewTabs';
import { showAlert } from 'components/shared/Alert/Alert';
import CustomPagination from 'components/shared/CustomPagination/CustomPagination';
import Header from 'components/shared/Header/Header';
import Allowed from 'components/shared/Permissions/Allowed';

import DeleteTreatmentPlanConfirm from './DeleteTreatmentPlanConfirm';
import FooterWrapperTreatmentPlan from './FooterWraperTreatmentPlan';
import TreatmentPlanModalTable from './TreatmentPlanModalTable';
import TreatmentPlanTable from './TreatmentPlanTable';
import { TreatmentPlanWidgetHeader } from './TreatmentPlanWidgetHeader';
import { getTreatmentPlanParams, treatmentPlanCols } from './lib/helpers';

export function TreatmentPlanMainTable({
  props,
  treatmentPlanToDelete,
  setTreatmentPlanToDelete,
  showTPModal,
  setShowTPModal,
  fromPracticeIQueue = false
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    limit,
    page,
    sort,
    setPage,
    filters,
    setFilters,
    initialFilters,
    defaultFilters,
    setInitialFilters,
    isEditingFilter,
    setIsEditingFilter
  } = useTableContext();
  const { selectedTreatmentPlan, setSelectedTreatmentPlan } = useClinicalNoteContext();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showAllRowsTable, setShowAllRowsTable] = useState(false);
  const [tpDeleteLoading, setTpDeleteLoading] = useState(false);
  const category = fromPracticeIQueue ? 'treatment_plan_iqueue' : '';

  const queryClient = useQueryClient();

  const params = getTreatmentPlanParams({ id, filters, page, limit, sort, fromPracticeIQueue });

  const { data } = useTreatmentPlan(params);

  const treatmentPlan = data?.treatment_plans || [];
  const count = data?.count || 0;

  const handleDeleteTreatmentPlan = async () => {
    setTpDeleteLoading(true);
    if (treatmentPlanToDelete) {
      await mutateDeleteTreatmentPlan.mutateAsync();
    }
    setTpDeleteLoading(false);
  };

  const mutateDeleteTreatmentPlan = useMutation({
    mutationFn: () => deleteTreatmentPlan(navigate, { treatmentIds: [treatmentPlanToDelete] }),
    onSuccess: () => {
      setTreatmentPlanToDelete(null);
      showAlert({
        title: `Treatment Plan`,
        message: 'Treatment plan deleted successfully!',
        color: 'success'
      });

      queryClient.invalidateQueries(['treatmentPlan', [params]]);
    }
  });

  const onCellClicked = (e) => {
    if (['checkbox_selection', 'actions'].includes(e.column.getColId())) return;

    if (!e?.data) return;

    setShowTPModal({ open: true, loadView: 'preview' });
    setSelectedTreatmentPlan(e?.data);
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {!fromPracticeIQueue && (
        <TreatmentPlanWidgetHeader setShowTPModal={setShowTPModal} props={props} />
      )}

      <Allowed
        requiredPermissions="clinical_note.read"
        showMessage={true}
        showIllustration={true}
        messageClassName="font-medium text-neutral-500">
        <div className="flex flex-1 flex-col overflow-y-auto">
          {fromPracticeIQueue && (
            <Header title="Treatment Plans">
              <ViewTabs
                category="treatment_plan_iqueue"
                title="Treatment Plans"
                filters={filters}
                setFilters={setFilters}
                defaultFilters={defaultFilters}
                setInitialFilters={setInitialFilters}
                isEditingFilter={isEditingFilter}
                setIsEditingFilter={setIsEditingFilter}
              />
            </Header>
          )}
          {fromPracticeIQueue && (
            <TableFilters
              filters={filters}
              initialFilters={initialFilters}
              setFilters={setFilters}
              category={category}
              defaultFilters={defaultFilters}
            />
          )}
          {!fromPracticeIQueue && (
            <FooterWrapperTreatmentPlan
              treatmentPlan={treatmentPlan}
              setShowAllRowsTable={setShowAllRowsTable}
              onCellClicked={onCellClicked}
            />
          )}

          {showConfirmationModal && (
            <SignModal
              setShowConfirmationModal={setShowConfirmationModal}
              showConfirmationModal={showConfirmationModal}
              documentId={selectedTreatmentPlan?.id}
              documentTypes={['treatment_plan']}
              asPatient={true}
            />
          )}

          {fromPracticeIQueue && (
            <TreatmentPlanTable
              data={treatmentPlan}
              showMultiActions={true}
              onCellClicked={onCellClicked}
            />
          )}

          {treatmentPlanToDelete && (
            <DeleteTreatmentPlanConfirm
              treatmentPlanToDelete={treatmentPlanToDelete}
              loading={tpDeleteLoading}
              handleClose={() => setTreatmentPlanToDelete(null)}
              handleDeleteTreatmentPlan={handleDeleteTreatmentPlan}
            />
          )}

          {fromPracticeIQueue && (
            <div className="flex items-center justify-between bg-white p-3">
              <CustomStatusBarCount
                data={treatmentPlan}
                page={page}
                limit={limit}
                count={count}
                pagination
              />
              <CustomPagination
                onPageChange={(newPage) => setPage(newPage)}
                totalItems={count ? count : null}
                page={page}
                perPage={limit}
              />
            </div>
          )}

          <TableContextProvider
            cols={treatmentPlanCols({
              setSelectedTreatmentPlan,
              setTreatmentPlanToDelete,
              setShowTPModal,
              showCheckboxes: true
            })}
            name="patient_treatment_plans_modal">
            <TreatmentPlanModalTable
              showAllRowsTable={showAllRowsTable}
              setShowAllRowsTable={setShowAllRowsTable}
              onCellClicked={onCellClicked}
              treatmentPlan={treatmentPlan}
              showMultiActions={true}
            />
          </TableContextProvider>

          {io(showTPModal) && showTPModal?.open && (
            <TPModal
              loadView={showTPModal?.loadView}
              isOpen={showTPModal?.open}
              handleClose={() => setShowTPModal({ open: false, loadView: 'list' })}
              outsideCN={true}
              initialView={showTPModal?.loadView !== 'list'}
            />
          )}
        </div>
      </Allowed>
    </div>
  );
}
